import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  DollarSign,
  Download,
  Plus,
  TrendingUp,
  TrendingDown,
  Search,
  Trash2,
  Edit3,
  X,
  Check,
  FileText,
  Printer,
  CreditCard,
  User,
  Clock,
  Layers,
  Receipt,
  CheckCircle2,
  AlertTriangle,
  Mail,
  MessageCircle,
  Loader2,
} from 'lucide-react';
import { api, resolveApiUrl } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { SkeletonStat, SkeletonTable } from '@/components/ui/skeleton.js';
import { PlatformBadge } from '@/components/ui/PlatformBadge.js';
import { cleanPhoneForWhatsApp } from '@/lib/clientUtils.js';
import {
  Invoice,
  InvoiceLineItem,
  InvoiceStatus,
  Client,
  Project,
} from '@/types/index.js';
import { PlatformProfitabilityCard } from '@/components/platform/PlatformProfitabilityCard.js';

interface Transaction {
  id: string;
  type: 'income' | 'expense';
  category: string;
  amount: number;
  currency: string;
  date: string;
  status: string;
  notes?: string | null;
  project_name?: string | null;
}

interface FinanceSummary {
  total_received?: number;
  total_paid?: number;
  profit?: number;
  total_income?: number;
  total_expenses?: number;
  net_profit?: number;
  pending_receivables?: number;
  currency?: string;
  // Invoicing KPIs
  total_invoiced?: number;
  collected_revenue?: number;
  outstanding_receivables?: number;
  overdue_invoices_count?: number;
  overdue_amount?: number;
  revenue_by_platform?: {
    Direct?: number;
    Upwork?: number;
    Fiverr?: number;
    Other?: number;
    [key: string]: number | undefined;
  };
}

export function FinancePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  if (user && user.role !== 'owner') {
    return (
      <UnauthorizedPage
        requiredRoles={['owner']}
        message="The finance ledger, invoices, and monetary metrics are strictly restricted to agency owners."
      />
    );
  }

  // Active module tab
  const initialTab = searchParams.get('tab') === 'cashflow' ? 'cashflow' : 'invoices';
  const [activeViewTab, setActiveViewTab] = useState<'invoices' | 'cashflow'>(initialTab);

  // Global / Shared Data
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // =========================================================================
  // INVOICING MODULE STATE
  // =========================================================================
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('');
  const [invoiceClientFilter, setInvoiceClientFilter] = useState(searchParams.get('client_id') || '');
  const [invoiceProjectFilter, setInvoiceProjectFilter] = useState(searchParams.get('project_id') || '');
  const [invoiceDateFilter, setInvoiceDateFilter] = useState('');
  const [invoiceSortField, setInvoiceSortField] = useState<string>('issue_date');
  const [invoiceSortDir, setInvoiceSortDir] = useState<'asc' | 'desc'>('desc');
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState<number>(1);

  // Invoice Modals
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [deleteInvoiceTarget, setDeleteInvoiceTarget] = useState<{ id: string; name: string } | null>(null);

  // Create Invoice Form State
  const defaultIssueDate = new Date().toISOString().substring(0, 10);
  const defaultDueDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().substring(0, 10);
  })();

  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '',
    client_id: '',
    client_name: '',
    client_email: '',
    project_id: '',
    project_name: '',
    platform: 'Direct',
    currency: 'USD',
    issue_date: defaultIssueDate,
    due_date: defaultDueDate,
    payment_notes: 'Payment is due within 14 days of invoice date. Thank you for your business.',
    discount: 0,
    tax_percent: 0,
    line_items: [
      {
        id: 'li_init_1',
        description: 'Web development & engineering sprint deliverable',
        quantity: 1,
        unit_price: 2500,
        amount: 2500,
      },
    ],
  });

  // Payment Form State
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().substring(0, 10),
    amount: '',
    payment_method: 'Bank Transfer',
    transaction_ref: '',
    notes: '',
  });

  // =========================================================================
  // CASHFLOW LEDGER MODULE STATE
  // =========================================================================
  const [finances, setFinances] = useState<Transaction[]>([]);
  const [financesLoading, setFinancesLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortField, setSortField] = useState<string>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Cashflow Modals
  const [showModal, setShowModal] = useState(false);
  const [newTx, setNewTx] = useState({
    type: 'income',
    category: 'Milestone Payment',
    amount: '',
    date: new Date().toISOString().substring(0, 10),
    currency: 'USD',
    notes: '',
  });

  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const [editForm, setEditForm] = useState({
    type: 'income',
    category: '',
    amount: '',
    date: '',
    currency: 'USD',
    notes: '',
  });

  const [deleteTxTarget, setDeleteTxTarget] = useState<{ id: string; name: string } | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Close modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowCreateInvoiceModal(false);
        setShowPaymentModal(false);
        setPreviewInvoice(null);
        setEditingTx(null);
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch Invoices, Finances, and Summary
  const fetchAllData = async () => {
    try {
      const [invRes, finRes, sumRes, cltRes, prjRes] = await Promise.all([
        api.get<{ data: Invoice[] }>('/api/invoices').catch(() => ({ data: [] })),
        api.get<{ data: Transaction[] }>('/api/v1/finances').catch(() => ({ data: [] })),
        api.get<{ data: FinanceSummary }>('/api/finance/summary').catch(() => null),
        api.get<{ data: Client[] }>('/api/v1/clients').catch(() => ({ data: [] })),
        api.get<{ data: Project[] }>('/api/v1/projects').catch(() => ({ data: [] })),
      ]);

      const rawInvoices = Array.isArray(invRes?.data) ? invRes.data : Array.isArray(invRes) ? invRes : [];
      setInvoices(rawInvoices);

      const rawFinances = Array.isArray(finRes?.data) ? finRes.data : Array.isArray(finRes) ? finRes : [];
      setFinances(rawFinances);

      const rawSummary = (sumRes && 'data' in sumRes && sumRes.data)
        ? (sumRes.data as FinanceSummary)
        : (sumRes && !('data' in sumRes) ? (sumRes as unknown as FinanceSummary) : null);
      setSummary(rawSummary || null);

      const rawClients = Array.isArray(cltRes?.data) ? cltRes.data : Array.isArray(cltRes) ? cltRes : [];
      setClients(rawClients);

      const rawProjects = Array.isArray(prjRes?.data) ? prjRes.data : Array.isArray(prjRes) ? prjRes : [];
      setProjects(rawProjects);
    } catch (e) {
      console.error('Failed to load finance data:', e);
    } finally {
      setInvoicesLoading(false);
      setFinancesLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Handle URL deep link action
  useEffect(() => {
    const action = searchParams.get('action');
    const clientId = searchParams.get('client_id');
    const projectId = searchParams.get('project_id');

    if (action === 'create_invoice') {
      setActiveViewTab('invoices');
      setShowCreateInvoiceModal(true);

      if (clientId && clients.length > 0) {
        const foundClient = clients.find((c) => c.id === clientId);
        if (foundClient) {
          handleSelectClient(foundClient.id, foundClient);
        }
      }

      if (projectId && projects.length > 0) {
        const foundProject = projects.find((p) => p.id === projectId);
        if (foundProject) {
          handleSelectProject(foundProject.id, foundProject);
        }
      }
    }
  }, [searchParams, clients, projects]);

  const activeCurrency = summary?.currency || (invoices.length > 0 ? invoices[0]?.currency : 'USD') || 'USD';

  // Export handlers
  const handleExportInvoicesCsv = () => {
    const url = resolveApiUrl('/api/invoices/export');
    window.open(url, '_blank');
  };

  const handleExportCashflowCsv = () => {
    const url = resolveApiUrl('/api/v1/finances/export');
    window.open(url, '_blank');
  };

  // =========================================================================
  // INVOICING LINE ITEM CALCULATORS
  // =========================================================================
  const calculateTotals = (
    items: InvoiceLineItem[],
    discountNum: number,
    taxPercentNum: number
  ) => {
    const subtotal = items.reduce((acc, item) => acc + (Number(item.amount) || 0), 0);
    const safeDiscount = Math.max(0, Number(discountNum) || 0);
    const taxableAmount = Math.max(0, subtotal - safeDiscount);
    const safeTaxPercent = Math.max(0, Number(taxPercentNum) || 0);
    const taxAmount = Math.round(taxableAmount * (safeTaxPercent / 100) * 100) / 100;
    const totalAmount = Math.round((taxableAmount + taxAmount) * 100) / 100;
    return { subtotal, discount: safeDiscount, taxPercent: safeTaxPercent, taxAmount, totalAmount };
  };

  const formTotals = useMemo(() => {
    return calculateTotals(invoiceForm.line_items, invoiceForm.discount, invoiceForm.tax_percent);
  }, [invoiceForm.line_items, invoiceForm.discount, invoiceForm.tax_percent]);

  const handleLineItemChange = (index: number, field: 'description' | 'quantity' | 'unit_price', value: any) => {
    setInvoiceForm((prev) => {
      const nextItems = [...prev.line_items];
      const target = { ...nextItems[index], [field]: value };
      const qty = field === 'quantity' ? Number(value) || 0 : Number(target.quantity) || 0;
      const price = field === 'unit_price' ? Number(value) || 0 : Number(target.unit_price) || 0;
      target.amount = Math.round(qty * price * 100) / 100;
      nextItems[index] = target;
      return { ...prev, line_items: nextItems };
    });
  };

  const handleAddLineItem = () => {
    setInvoiceForm((prev) => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          id: `li_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          description: '',
          quantity: 1,
          unit_price: 500,
          amount: 500,
        },
      ],
    }));
  };

  const handleRemoveLineItem = (index: number) => {
    if (invoiceForm.line_items.length <= 1) {
      alert('Invoice must have at least one line item.');
      return;
    }
    setInvoiceForm((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((_, idx) => idx !== index),
    }));
  };

  const handleSelectClient = (clientId: string, clientObj?: Client) => {
    const target = clientObj || clients.find((c) => c.id === clientId);
    if (!target) {
      setInvoiceForm((prev) => ({ ...prev, client_id: '', client_name: '', client_email: '' }));
      return;
    }
    setInvoiceForm((prev) => ({
      ...prev,
      client_id: target.id,
      client_name: target.name,
      client_email: target.email || '',
      currency: target.currency || prev.currency,
      platform: target.platform ? target.platform.charAt(0).toUpperCase() + target.platform.slice(1) : prev.platform,
    }));
  };

  const handleSelectProject = (projectId: string, projectObj?: Project) => {
    const target = projectObj || projects.find((p) => p.id === projectId);
    if (!target) {
      setInvoiceForm((prev) => ({ ...prev, project_id: '', project_name: '' }));
      return;
    }
    setInvoiceForm((prev) => ({
      ...prev,
      project_id: target.id,
      project_name: target.name,
      platform: target.platform ? target.platform.charAt(0).toUpperCase() + target.platform.slice(1) : prev.platform,
    }));
  };

  // Submit Create Invoice
  const handleCreateInvoiceSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!invoiceForm.client_name.trim()) {
      alert('Client Name is required');
      return;
    }
    if (invoiceForm.line_items.length === 0) {
      alert('At least one line item is required');
      return;
    }

    try {
      const payload = {
        invoice_number: invoiceForm.invoice_number.trim() || undefined,
        client_id: invoiceForm.client_id || undefined,
        client_name: invoiceForm.client_name.trim(),
        client_email: invoiceForm.client_email.trim() || undefined,
        project_id: invoiceForm.project_id || undefined,
        project_name: invoiceForm.project_name.trim() || undefined,
        platform: invoiceForm.platform || 'Direct',
        currency: invoiceForm.currency || 'USD',
        issue_date: invoiceForm.issue_date,
        due_date: invoiceForm.due_date || undefined,
        payment_notes: invoiceForm.payment_notes.trim() || undefined,
        discount: Number(invoiceForm.discount) || 0,
        tax_percent: Number(invoiceForm.tax_percent) || 0,
        line_items: invoiceForm.line_items.map((li) => ({
          description: li.description.trim() || 'Service Deliverable',
          quantity: Number(li.quantity) || 1,
          unit_price: Number(li.unit_price) || 0,
        })),
      };

      await api.post('/api/invoices', payload);
      setShowCreateInvoiceModal(false);
      showToast('Invoice created successfully!');
      fetchAllData();
    } catch (e: any) {
      alert(e.message || 'Failed to create invoice');
    }
  };

  // Open Record Payment Modal
  const handleOpenPayment = (inv: Invoice) => {
    setPaymentInvoice(inv);
    setPaymentForm({
      payment_date: new Date().toISOString().substring(0, 10),
      amount: String(inv.balance_due > 0 ? inv.balance_due : inv.total_amount),
      payment_method: inv.platform === 'Fiverr' ? 'Fiverr' : inv.platform === 'Upwork' ? 'Upwork' : 'Bank Transfer',
      transaction_ref: '',
      notes: '',
    });
    setShowPaymentModal(true);
  };

  const handleRecordPaymentSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!paymentInvoice) return;
    const amountNum = Number(paymentForm.amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert('Payment amount must be greater than zero.');
      return;
    }

    try {
      await api.post(`/api/invoices/${paymentInvoice.id}/payments`, {
        payment_date: paymentForm.payment_date,
        amount: amountNum,
        payment_method: paymentForm.payment_method,
        transaction_ref: paymentForm.transaction_ref.trim() || undefined,
        notes: paymentForm.notes.trim() || undefined,
      });

      setShowPaymentModal(false);
      setPaymentInvoice(null);
      showToast(`Payment of ${paymentInvoice.currency} ${amountNum.toLocaleString()} recorded successfully!`);
      fetchAllData();
    } catch (e: any) {
      alert(e.message || 'Failed to record invoice payment');
    }
  };

  // Delete Invoice
  const handleDeleteInvoice = (inv: Invoice) => {
    setDeleteInvoiceTarget({
      id: inv.id,
      name: `Invoice ${inv.invoice_number} (${inv.client_name} - ${inv.currency} ${inv.total_amount.toLocaleString()})`,
    });
  };

  const confirmDeleteInvoice = async () => {
    if (!deleteInvoiceTarget) return;
    try {
      await api.delete(`/api/invoices/${deleteInvoiceTarget.id}`);
      setDeleteInvoiceTarget(null);
      showToast('Invoice deleted successfully.');
      fetchAllData();
    } catch (e: any) {
      alert(e.message || 'Failed to delete invoice');
    }
  };

  // 1-Click Invoice Email Dispatch & WhatsApp Payment Links
  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);

  const generateWhatsAppPaymentUrl = (inv: Invoice) => {
    const client = clients.find(
      (c) => c.id === inv.client_id || (c.name && inv.client_name && c.name.toLowerCase() === inv.client_name.toLowerCase())
    );
    const rawPhone = client?.mobile || client?.phone;
    const phoneDigits = cleanPhoneForWhatsApp(rawPhone);

    const lines = [
      `*INVOICE PAYMENT REQUEST — TECHOSOLUTION OS* 🧾`,
      ``,
      `Hello *${inv.client_name}*,`,
      `Here is your invoice *#${inv.invoice_number}* from Techosolution:`,
      ``,
      `• *Total Amount:* ${inv.currency} ${Number(inv.total_amount).toLocaleString()}`,
      `• *Amount Paid:* ${inv.currency} ${Number(inv.amount_paid).toLocaleString()}`,
      `• *Balance Due:* ${inv.currency} ${Number(inv.balance_due).toLocaleString()}`,
      `• *Due Date:* ${inv.due_date || 'On Receipt'}`,
      inv.project_name ? `• *Project:* ${inv.project_name}` : null,
      ``,
      `*Accepted Payment Methods:*`,
      `1. Payoneer Direct Transfer: billing@techosolution.com`,
      `2. Bank Wire Transfer (SWIFT / IBAN wire coordinates available upon request)`,
      `3. Stripe / Online Card Payment`,
      ``,
      `View Invoice Online: https://os.techosolution.com/#/finance?invoice_id=${inv.id}`,
      ``,
      `Thank you for your business!`,
      `— Techosolution Agency Operations`,
    ]
      .filter(Boolean)
      .join('\n');

    const encoded = encodeURIComponent(lines);
    return phoneDigits ? `https://wa.me/${phoneDigits}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
  };

  const handleSendInvoiceEmail = async (inv: Invoice, overrideEmail?: string) => {
    setSendingInvoiceId(inv.id);
    try {
      const res = await api.post<{ data: Invoice; message?: string }>(
        `/api/v1/invoices/${inv.id}/send`,
        overrideEmail ? { email: overrideEmail } : {}
      );
      const updatedInv = res?.data || {
        ...inv,
        status: inv.status === 'draft' ? 'unpaid' : inv.status,
        sent_at: new Date().toISOString(),
      };
      setInvoices((prev) => prev.map((item) => (item.id === inv.id ? updatedInv : item)));
      if (previewInvoice && previewInvoice.id === inv.id) {
        setPreviewInvoice(updatedInv);
      }
      showToast(`Invoice #${inv.invoice_number} dispatched to ${updatedInv.sent_to_email || inv.client_email || 'client'}!`);
      fetchAllData();
    } catch (e: any) {
      alert(e.message || 'Failed to dispatch invoice email');
    } finally {
      setSendingInvoiceId(null);
    }
  };

  // Sort & Filter Invoices
  const handleInvoiceSort = (field: string) => {
    if (invoiceSortField === field) {
      setInvoiceSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setInvoiceSortField(field);
      setInvoiceSortDir('asc');
    }
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const q = invoiceSearch.toLowerCase();
      const matchesSearch = q
        ? inv.invoice_number?.toLowerCase().includes(q) ||
          inv.client_name?.toLowerCase().includes(q) ||
          inv.project_name?.toLowerCase().includes(q) ||
          inv.payment_notes?.toLowerCase().includes(q)
        : true;

      const matchesStatus = invoiceStatusFilter ? inv.status === invoiceStatusFilter : true;
      const matchesClient = invoiceClientFilter ? inv.client_id === invoiceClientFilter || inv.client_name === invoiceClientFilter : true;
      const matchesProject = invoiceProjectFilter ? inv.project_id === invoiceProjectFilter : true;
      const matchesDate = invoiceDateFilter ? (inv.issue_date && inv.issue_date.startsWith(invoiceDateFilter)) : true;

      return matchesSearch && matchesStatus && matchesClient && matchesProject && matchesDate;
    });
  }, [invoices, invoiceSearch, invoiceStatusFilter, invoiceClientFilter, invoiceProjectFilter, invoiceDateFilter]);

  const sortedInvoices = useMemo(() => {
    return [...filteredInvoices].sort((a: any, b: any) => {
      let aVal = a[invoiceSortField];
      let bVal = b[invoiceSortField];
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      if (['total_amount', 'balance_due', 'amount_paid'].includes(invoiceSortField)) {
        return invoiceSortDir === 'asc' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      }
      if (typeof aVal === 'string') {
        return invoiceSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return invoiceSortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [filteredInvoices, invoiceSortField, invoiceSortDir]);

  const invoicePageSize = 15;
  const invoiceTotalPages = Math.max(1, Math.ceil(sortedInvoices.length / invoicePageSize));
  const validInvoicePage = Math.min(invoiceCurrentPage, invoiceTotalPages);
  const paginatedInvoices = sortedInvoices.slice((validInvoicePage - 1) * invoicePageSize, validInvoicePage * invoicePageSize);

  // Status Badge Helper
  const renderStatusBadge = (status: InvoiceStatus) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wide">
            Paid
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 uppercase tracking-wide">
            Partial
          </span>
        );
      case 'overdue':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200 uppercase tracking-wide animate-pulse">
            Overdue
          </span>
        );
      case 'unpaid':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wide">
            Unpaid
          </span>
        );
      case 'draft':
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wide">
            Draft
          </span>
        );
    }
  };

  // =========================================================================
  // CASHFLOW LEDGER HANDLERS
  // =========================================================================
  const handleCreateTx = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/finances', { ...newTx, amount: Number(newTx.amount) });
      setShowModal(false);
      setNewTx({
        type: 'income',
        category: 'Milestone Payment',
        amount: '',
        date: new Date().toISOString().substring(0, 10),
        currency: activeCurrency,
        notes: '',
      });
      showToast('Transaction recorded successfully!');
      fetchAllData();
    } catch (e: any) {
      alert(e.message || 'Failed to create financial transaction');
    }
  };

  const handleOpenEditTx = (tx: Transaction) => {
    setEditingTx(tx);
    setEditForm({
      type: tx.type,
      category: tx.category || '',
      amount: String(tx.amount || ''),
      date: tx.date ? tx.date.substring(0, 10) : new Date().toISOString().substring(0, 10),
      currency: tx.currency || activeCurrency,
      notes: tx.notes || '',
    });
  };

  const handleSaveEditTx = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingTx) return;
    try {
      await api.patch(`/api/v1/finances/${editingTx.id}`, {
        ...editForm,
        amount: Number(editForm.amount),
      });
      setEditingTx(null);
      showToast('Transaction updated successfully!');
      fetchAllData();
    } catch (e: any) {
      alert(e.message || 'Failed to update transaction');
    }
  };

  const handleDeleteTx = (id: string, name?: string) => {
    setDeleteTxTarget({ id, name: name || 'this financial transaction' });
  };

  const confirmDeleteTx = async () => {
    if (!deleteTxTarget) return;
    try {
      await api.delete(`/api/v1/finances/${deleteTxTarget.id}`);
      setDeleteTxTarget(null);
      showToast('Transaction deleted successfully.');
      fetchAllData();
    } catch (e: any) {
      alert(e.message || 'Failed to delete transaction');
    }
  };

  const handleCashflowSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const uniqueCategories = Array.from(new Set(finances.map((tx) => tx.category).filter(Boolean)));
  const uniqueCurrencies = Array.from(new Set(finances.map((tx) => tx.currency).filter(Boolean)));

  const filteredFinances = finances.filter((tx) => {
    const q = search.toLowerCase();
    const matchesSearch = q
      ? (tx.category && tx.category.toLowerCase().includes(q)) ||
        (tx.notes && tx.notes.toLowerCase().includes(q)) ||
        (tx.project_name && tx.project_name.toLowerCase().includes(q))
      : true;
    const matchesType = typeFilter ? tx.type === typeFilter : true;
    const matchesCategory = categoryFilter ? tx.category === categoryFilter : true;
    const matchesCurrency = currencyFilter ? tx.currency === currencyFilter : true;
    const matchesDate = dateFilter ? (tx.date && tx.date.startsWith(dateFilter)) : true;
    return matchesSearch && matchesType && matchesCategory && matchesCurrency && matchesDate;
  });

  const sortedFinances = [...filteredFinances].sort((a: any, b: any) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (sortField === 'amount') {
      return sortDir === 'asc' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
    }
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const cashflowPageSize = 25;
  const cashflowTotalPages = Math.max(1, Math.ceil(sortedFinances.length / cashflowPageSize));
  const validCashflowPage = Math.min(currentPage, cashflowTotalPages);
  const paginatedFinances = sortedFinances.slice((validCashflowPage - 1) * cashflowPageSize, validCashflowPage * cashflowPageSize);

  // Platform Revenue Stats
  const platformRevenue = {
    Direct: summary?.revenue_by_platform?.Direct ?? (summary as any)?.by_platform?.direct?.total_revenue ?? invoices.filter(i => (i.platform || '').toLowerCase() === 'direct').reduce((s, i) => s + (Number(i.amount_paid) || 0), 0),
    Upwork: summary?.revenue_by_platform?.Upwork ?? (summary as any)?.by_platform?.upwork?.total_revenue ?? invoices.filter(i => (i.platform || '').toLowerCase() === 'upwork').reduce((s, i) => s + (Number(i.amount_paid) || 0), 0),
    Fiverr: summary?.revenue_by_platform?.Fiverr ?? (summary as any)?.by_platform?.fiverr?.total_revenue ?? invoices.filter(i => (i.platform || '').toLowerCase() === 'fiverr').reduce((s, i) => s + (Number(i.amount_paid) || 0), 0),
    Other: summary?.revenue_by_platform?.Other ?? (summary as any)?.by_platform?.other?.total_revenue ?? invoices.filter(i => !['direct', 'upwork', 'fiverr'].includes((i.platform || '').toLowerCase())).reduce((s, i) => s + (Number(i.amount_paid) || 0), 0),
  };
  const totalPlatformCollected = Math.max(1, (summary?.collected_revenue ?? summary?.total_received ?? Object.values(platformRevenue).reduce((a, b) => a + b, 0)) || 1);

  return (
    <div className="space-y-6">
      {/* Toast Feedback */}
      {toastMessage && (
        <div className="fixed top-16 right-6 z-50 bg-brand-navy text-white px-4 py-2.5 rounded-lg shadow-xl text-xs flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4 text-brand-teal" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header and Mode Selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <h1 className="page-title text-2xl font-bold text-brand-navy dark:text-brand-ink">
              Agency Finance & Billing
            </h1>
            <Badge variant="outline" className="text-[10px] uppercase font-mono tracking-wider border-brand-teal text-brand-teal">
              Owner Restricted
            </Badge>
          </div>
          <p className="helper-text mt-1 text-slate-500 dark:text-brand-muted text-xs">
            Manage agency invoicing, milestone billing, multi-platform revenue, and operating cashflow.
          </p>
        </div>

        {/* View Switcher Tabs */}
        <div className="flex items-center space-x-2 bg-slate-100 dark:bg-brand-surface p-1 rounded-lg border border-slate-200 dark:border-brand-border">
          <button
            type="button"
            onClick={() => setActiveViewTab('invoices')}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              activeViewTab === 'invoices'
                ? 'bg-white dark:bg-[#152724] text-brand-navy dark:text-white shadow-xs'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Receipt className="w-3.5 h-3.5 text-brand-teal" />
            <span>Invoices & Billing</span>
            <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] font-mono bg-brand-tint text-brand-ink">
              {invoices.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveViewTab('cashflow')}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              activeViewTab === 'cashflow'
                ? 'bg-white dark:bg-[#152724] text-brand-navy dark:text-white shadow-xs'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Layers className="w-3.5 h-3.5 text-indigo-500" />
            <span>Cashflow Ledger</span>
            <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] font-mono bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {finances.length}
            </span>
          </button>
        </div>
      </div>

      {/* =========================================================================
          VIEW 1: INVOICES & BILLING (PRIMARY)
         ========================================================================= */}
      {activeViewTab === 'invoices' && (
        <div className="space-y-6 animate-in fade-in duration-200">
          {/* Top Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-brand-surface p-3.5 rounded-lg border border-slate-200 dark:border-brand-border">
            <div className="text-xs text-slate-500 dark:text-brand-muted">
              Issuing branded client invoices, automated balance computation, and platform tracking.
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={handleExportInvoicesCsv}
                className="flex items-center space-x-1.5 text-xs font-semibold"
                title="Export all agency invoices to CSV"
              >
                <Download className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                <span>Export CSV</span>
              </Button>
              <Button
                onClick={() => {
                  setInvoiceForm({
                    invoice_number: '',
                    client_id: '',
                    client_name: '',
                    client_email: '',
                    project_id: '',
                    project_name: '',
                    platform: 'Direct',
                    currency: activeCurrency,
                    issue_date: defaultIssueDate,
                    due_date: defaultDueDate,
                    payment_notes: 'Payment is due within 14 days of invoice date. Thank you for your business.',
                    discount: 0,
                    tax_percent: 0,
                    line_items: [
                      {
                        id: `li_${Date.now()}`,
                        description: 'Engineering & delivery milestone',
                        quantity: 1,
                        unit_price: 2500,
                        amount: 2500,
                      },
                    ],
                  });
                  setShowCreateInvoiceModal(true);
                }}
                className="flex items-center space-x-1.5 text-xs font-semibold bg-brand-teal text-white hover:bg-brand-teal-action"
              >
                <Plus className="h-4 w-4" />
                <span>Create Invoice</span>
              </Button>
            </div>
          </div>

          {/* 4 Invoicing KPI Summary Cards */}
          {invoicesLoading && !summary ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Invoiced */}
              <Card className="hover:shadow-hover transition-all bg-white dark:bg-brand-surface">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between pb-1">
                  <span className="text-xs uppercase text-brand-muted font-bold tracking-wider">Total Invoiced</span>
                  <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
                    <FileText className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="result-number text-2xl font-bold text-brand-ink dark:text-white font-mono">
                    {activeCurrency} {((val: any) => { const n = Number(val); return isNaN(n) ? '0' : n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }); })(summary?.total_invoiced ?? invoices.reduce((acc, i) => acc + (Number(i.total_amount) || 0), 0))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Across all client billables</p>
                </CardContent>
              </Card>

              {/* Collected Revenue */}
              <Card className="hover:shadow-hover transition-all bg-white dark:bg-brand-surface">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between pb-1">
                  <span className="text-xs uppercase text-brand-muted font-bold tracking-wider">Collected Revenue</span>
                  <div className="p-1.5 rounded-lg bg-emerald-50 text-status-success dark:bg-emerald-950/40 dark:text-emerald-400">
                    <TrendingUp className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="result-number text-2xl font-bold text-status-success dark:text-emerald-400 font-mono">
                    {activeCurrency} {((val: any) => { const n = Number(val); return isNaN(n) ? '0' : n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }); })(summary?.collected_revenue ?? invoices.reduce((acc, i) => acc + (Number(i.amount_paid) || 0), 0))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Funds settled and cleared</p>
                </CardContent>
              </Card>

              {/* Outstanding Receivables */}
              <Card className="hover:shadow-hover transition-all bg-white dark:bg-brand-surface">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between pb-1">
                  <span className="text-xs uppercase text-brand-muted font-bold tracking-wider">Outstanding Due</span>
                  <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
                    <Clock className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="result-number text-2xl font-bold text-amber-600 dark:text-amber-400 font-mono">
                    {activeCurrency} {((val: any) => { const n = Number(val); return isNaN(n) ? '0' : n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }); })(summary?.outstanding_receivables ?? invoices.reduce((acc, i) => acc + (Number(i.balance_due) || 0), 0))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Pending client balances</p>
                </CardContent>
              </Card>

              {/* Overdue Invoices */}
              <Card className="hover:shadow-hover transition-all bg-white dark:bg-brand-surface border-l-4 border-l-red-500">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between pb-1">
                  <span className="text-xs uppercase text-brand-muted font-bold tracking-wider">Overdue Invoices</span>
                  <div className="p-1.5 rounded-lg bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="flex items-baseline space-x-2">
                    <span className="text-2xl font-bold text-red-600 dark:text-red-400 font-mono">
                      {summary?.overdue_invoices_count ?? invoices.filter((i) => i.status === 'overdue').length}
                    </span>
                    <span className="text-xs font-semibold text-slate-500 font-mono">
                      ({activeCurrency} {((val: any) => { const n = Number(val); return isNaN(n) ? '0' : n.toLocaleString(); })(summary?.overdue_amount ?? invoices.filter((i) => i.status === 'overdue').reduce((a, b) => a + (Number(b.balance_due) || 0), 0))})
                    </span>
                  </div>
                  <p className="text-[11px] text-red-500 mt-1">Requires immediate follow-up</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Multi-Platform Pipeline & Automated Net Profit Engine */}
          <PlatformProfitabilityCard
            invoices={invoices}
            projects={projects}
            className="mb-2"
          />

          {/* Platform Revenue Breakdown Strip */}
          <div className="bg-white dark:bg-brand-surface p-4 rounded-lg border border-slate-200 dark:border-brand-border space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-400 flex items-center space-x-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-brand-teal" />
                <span>Revenue Breakdown by Platform</span>
              </span>
              <span className="text-[11px] text-slate-400 font-mono">
                Total: {activeCurrency} {((val: any) => Number(val || 0).toLocaleString())(summary?.collected_revenue ?? 0)}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
              {[
                { name: 'Direct', amount: platformRevenue.Direct || 0, badge: 'direct' },
                { name: 'Upwork', amount: platformRevenue.Upwork || 0, badge: 'upwork' },
                { name: 'Fiverr', amount: platformRevenue.Fiverr || 0, badge: 'fiverr' },
                { name: 'Other', amount: platformRevenue.Other || 0, badge: 'other' },
              ].map((plat) => {
                const pct = Math.round((plat.amount / totalPlatformCollected) * 100);
                return (
                  <div
                    key={plat.name}
                    className="p-3 rounded-lg border border-slate-100 dark:border-brand-border bg-slate-50/60 dark:bg-brand-surface/40 flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between">
                      <PlatformBadge platform={plat.badge} size="xs" />
                      <span className="text-[10px] font-mono font-bold text-slate-400">{pct}%</span>
                    </div>
                    <div className="mt-2 text-base font-bold font-mono text-brand-navy dark:text-brand-ink">
                      {activeCurrency} {plat.amount.toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Invoices Filter Toolbar */}
          <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 bg-white dark:bg-brand-surface p-3.5 rounded-lg border border-slate-200 dark:border-brand-border">
            <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] sm:max-w-xs">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search invoice #, client, project..."
                  value={invoiceSearch}
                  onChange={(e) => {
                    setInvoiceSearch(e.target.value);
                    setInvoiceCurrentPage(1);
                  }}
                  className="pl-9 h-9 text-xs"
                />
              </div>

              {/* Status Filter Tabs / Dropdown */}
              <select
                value={invoiceStatusFilter}
                onChange={(e) => {
                  setInvoiceStatusFilter(e.target.value);
                  setInvoiceCurrentPage(1);
                }}
                className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>

              {/* Client Filter */}
              <select
                value={invoiceClientFilter}
                onChange={(e) => {
                  setInvoiceClientFilter(e.target.value);
                  setInvoiceCurrentPage(1);
                }}
                className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200 max-w-[180px] truncate"
              >
                <option value="">All Clients</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              {/* Project Filter */}
              <select
                value={invoiceProjectFilter}
                onChange={(e) => {
                  setInvoiceProjectFilter(e.target.value);
                  setInvoiceCurrentPage(1);
                }}
                className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200 max-w-[180px] truncate"
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>

              {/* Issue Date Filter */}
              <div className="flex items-center space-x-1">
                <input
                  type="date"
                  value={invoiceDateFilter}
                  onChange={(e) => {
                    setInvoiceDateFilter(e.target.value);
                    setInvoiceCurrentPage(1);
                  }}
                  className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2 text-xs text-slate-700 dark:text-slate-200"
                />
                {invoiceDateFilter && (
                  <button
                    type="button"
                    onClick={() => {
                      setInvoiceDateFilter('');
                      setInvoiceCurrentPage(1);
                    }}
                    className="text-[11px] text-brand-teal hover:underline px-1 font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Sort Controls */}
            <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400">
              <span className="font-medium text-slate-400">Sort:</span>
              <SortButton
                label="Date"
                field="issue_date"
                currentField={invoiceSortField}
                currentDirection={invoiceSortDir}
                onSort={handleInvoiceSort}
              />
              <SortButton
                label="Total"
                field="total_amount"
                currentField={invoiceSortField}
                currentDirection={invoiceSortDir}
                onSort={handleInvoiceSort}
              />
              <SortButton
                label="Due"
                field="balance_due"
                currentField={invoiceSortField}
                currentDirection={invoiceSortDir}
                onSort={handleInvoiceSort}
              />
              <SortButton
                label="Status"
                field="status"
                currentField={invoiceSortField}
                currentDirection={invoiceSortDir}
                onSort={handleInvoiceSort}
              />
            </div>
          </div>

          {/* Invoices List / Table */}
          <Card className="bg-white dark:bg-brand-surface border-slate-200 dark:border-brand-border">
            <CardHeader className="py-3 px-4 border-b border-slate-100 dark:border-brand-border flex flex-row items-center justify-between">
              <div className="flex items-center space-x-2">
                <CardTitle className="text-sm font-semibold text-slate-800 dark:text-white">
                  Invoices Ledger
                </CardTitle>
                <Badge variant="secondary" className="text-[10px] font-mono">
                  {sortedInvoices.length} invoices
                </Badge>
              </div>
            </CardHeader>
            <div className="divide-y divide-slate-100 dark:divide-brand-border">
              {invoicesLoading ? (
                <SkeletonTable rows={5} cols={5} />
              ) : paginatedInvoices.length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-xs space-y-2">
                  <Receipt className="w-8 h-8 text-slate-300 mx-auto" />
                  <p>No invoices found matching criteria.</p>
                </div>
              ) : (
                paginatedInvoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3 text-xs hover:bg-slate-50/70 dark:hover:bg-brand-surface/40 transition-colors"
                  >
                    {/* Invoice ID & Client Details */}
                    <div className="space-y-1.5 flex-1 min-w-[220px]">
                      <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                        <button
                          type="button"
                          onClick={() => setPreviewInvoice(inv)}
                          className="font-mono font-bold text-brand-teal hover:underline flex items-center space-x-1"
                          title="Click to view & print invoice"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          <span>{inv.invoice_number}</span>
                        </button>
                        {renderStatusBadge(inv.status)}
                        {inv.sent_at && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800 py-0 px-1.5 flex items-center space-x-1"
                            title={`Dispatched to ${inv.sent_to_email || inv.client_email} on ${new Date(inv.sent_at).toLocaleDateString()}`}
                          >
                            <Mail className="w-2.5 h-2.5" />
                            <span>Sent</span>
                          </Badge>
                        )}
                        <PlatformBadge platform={inv.platform} size="xs" />
                      </div>

                      <div className="text-slate-800 dark:text-slate-200 font-semibold flex items-center space-x-1.5">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        <span>{inv.client_name}</span>
                        {inv.project_name && (
                          <span className="text-slate-400 font-normal truncate">
                            • {inv.project_name}
                          </span>
                        )}
                      </div>

                      {inv.payment_notes && (
                        <p className="text-[11px] text-slate-500 dark:text-brand-muted line-clamp-1 italic">
                          {inv.payment_notes}
                        </p>
                      )}
                    </div>

                    {/* Dates & Timeline */}
                    <div className="text-slate-500 dark:text-slate-400 text-[11px] flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1">
                      <div>
                        <span className="text-slate-400">Issued: </span>
                        <span className="font-mono text-slate-700 dark:text-slate-300">{inv.issue_date}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Due: </span>
                        <span
                          className={`font-mono ${
                            inv.status === 'overdue' ? 'font-bold text-red-600' : 'text-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {inv.due_date || 'On Receipt'}
                        </span>
                      </div>
                    </div>

                    {/* Financial Figures & Balance */}
                    <div className="flex items-center justify-between lg:justify-end space-x-6 min-w-[190px]">
                      <div className="text-right">
                        <div className="text-slate-400 text-[10px] uppercase font-bold tracking-wider">Total</div>
                        <div className="font-mono font-bold text-sm text-slate-800 dark:text-slate-100">
                          {inv.currency} {Number(inv.total_amount).toLocaleString()}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-slate-400 text-[10px] uppercase font-bold tracking-wider">Balance Due</div>
                        <div
                          className={`font-mono font-bold text-sm ${
                            inv.balance_due > 0
                              ? inv.status === 'overdue'
                                ? 'text-red-600'
                                : 'text-amber-600'
                              : 'text-emerald-600'
                          }`}
                        >
                          {inv.currency} {Number(inv.balance_due).toLocaleString()}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 pl-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleSendInvoiceEmail(inv)}
                          disabled={sendingInvoiceId === inv.id}
                          className="h-8 w-8 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                          title="Dispatch invoice email to client"
                        >
                          {sendingInvoiceId === inv.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Mail className="h-3.5 w-3.5" />
                          )}
                        </Button>

                        <a
                          href={generateWhatsAppPaymentUrl(inv)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                          title="Share invoice & payment link via WhatsApp"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </a>

                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setPreviewInvoice(inv)}
                          className="h-8 w-8 text-slate-500 hover:text-brand-navy dark:hover:text-white"
                          title="View / Print Invoice"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>

                        {inv.balance_due > 0 && inv.status !== 'paid' && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleOpenPayment(inv)}
                            className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                            title="Record Payment"
                          >
                            <DollarSign className="h-4 w-4" />
                          </Button>
                        )}

                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteInvoice(inv)}
                          className="h-8 w-8 text-slate-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                          title="Delete Invoice"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <Pagination
              totalItems={sortedInvoices.length}
              currentPage={validInvoicePage}
              pageSize={invoicePageSize}
              onPageChange={setInvoiceCurrentPage}
            />
          </Card>
        </div>
      )}

      {/* =========================================================================
          VIEW 2: CASHFLOW LEDGER & EXPENSES (SECONDARY)
         ========================================================================= */}
      {activeViewTab === 'cashflow' && (
        <div className="space-y-6 animate-in fade-in duration-200">
          {/* Top Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-brand-surface p-3.5 rounded-lg border border-slate-200 dark:border-brand-border">
            <div className="text-xs text-slate-500 dark:text-brand-muted">
              Agency monetary cashflow, operating expenditures, developer disbursements, and milestone receivables.
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={handleExportCashflowCsv}
                className="flex items-center space-x-1.5 text-xs font-semibold"
                title="Export financial ledger to CSV"
              >
                <Download className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                <span>Export CSV</span>
              </Button>
              <Button
                onClick={() => setShowModal(true)}
                className="flex items-center space-x-1.5 text-xs font-semibold bg-brand-teal text-white hover:bg-brand-teal-action"
              >
                <Plus className="h-4 w-4" />
                <span>Add Transaction</span>
              </Button>
            </div>
          </div>

          {/* Cashflow Summary Cards */}
          {financesLoading && !summary ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
            </div>
          ) : summary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="hover:shadow-hover transition-all bg-white dark:bg-brand-surface">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between pb-1">
                  <span className="text-xs uppercase text-brand-muted font-bold tracking-wider">Total Received</span>
                  <div className="p-1.5 rounded-lg bg-emerald-50 text-status-success dark:bg-emerald-950/40 dark:text-emerald-400">
                    <TrendingUp className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="result-number text-2xl font-bold text-status-success font-mono">
                    {activeCurrency} {((val: any) => { const n = Number(val); return isNaN(n) ? '0' : n.toLocaleString(); })(summary.total_received ?? summary.total_income ?? 0)}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Inflow ledger entries</p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-hover transition-all bg-white dark:bg-brand-surface">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between pb-1">
                  <span className="text-xs uppercase text-brand-muted font-bold tracking-wider">Total Paid</span>
                  <div className="p-1.5 rounded-lg bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    <TrendingDown className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="result-number text-2xl font-bold text-brand-ink dark:text-white font-mono">
                    {activeCurrency} {((val: any) => { const n = Number(val); return isNaN(n) ? '0' : n.toLocaleString(); })(summary.total_paid ?? summary.total_expenses ?? 0)}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Agency operating costs</p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-hover transition-all bg-white dark:bg-brand-surface">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between pb-1">
                  <span className="text-xs uppercase text-brand-muted font-bold tracking-wider">Net Profit</span>
                  <div className="p-1.5 rounded-lg bg-brand-tint text-brand dark:bg-brand-tint/20 dark:text-brand-lime">
                    <DollarSign className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="result-number text-2xl font-bold text-brand dark:text-brand-lime font-mono">
                    {activeCurrency} {((val: any) => { const n = Number(val); return isNaN(n) ? '0' : n.toLocaleString(); })(summary.profit ?? summary.net_profit ?? 0)}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Net agency margin</p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-hover transition-all bg-white dark:bg-brand-surface">
                <CardHeader className="py-3 px-4 flex flex-row items-center justify-between pb-1">
                  <span className="text-xs uppercase text-brand-muted font-bold tracking-wider">Pending Receivables</span>
                  <div className="p-1.5 rounded-lg bg-amber-100/60 text-status-warning dark:bg-amber-950/40 dark:text-amber-400">
                    <Clock className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 font-mono">
                    {activeCurrency} {((val: any) => { const n = Number(val); return isNaN(n) ? '0' : n.toLocaleString(); })(summary.pending_receivables ?? 0)}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Estimated pipeline receipts</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Cashflow Toolbar */}
          <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 bg-white dark:bg-brand-surface p-3 rounded-lg border border-slate-200 dark:border-brand-border">
            <div className="flex flex-wrap items-center gap-2.5 w-full xl:w-auto">
              <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search transactions..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9 h-9 text-xs"
                />
              </div>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
              >
                <option value="">All Types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
              >
                <option value="">All Categories</option>
                {uniqueCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {uniqueCurrencies.length > 1 && (
                <select
                  value={currencyFilter}
                  onChange={(e) => {
                    setCurrencyFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200 font-mono"
                >
                  <option value="">All Currencies</option>
                  {uniqueCurrencies.map((cur) => (
                    <option key={cur} value={cur}>
                      {cur}
                    </option>
                  ))}
                </select>
              )}
              <div className="flex items-center space-x-1">
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2 text-xs text-slate-700 dark:text-slate-200"
                />
                {dateFilter && (
                  <button
                    type="button"
                    onClick={() => {
                      setDateFilter('');
                      setCurrentPage(1);
                    }}
                    className="text-[11px] text-brand-teal hover:underline px-1 font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3 text-xs text-slate-500">
              <span className="font-medium text-slate-400">Sort:</span>
              <SortButton label="Date" field="date" currentField={sortField} currentDirection={sortDir} onSort={handleCashflowSort} />
              <SortButton label="Amount" field="amount" currentField={sortField} currentDirection={sortDir} onSort={handleCashflowSort} />
              <SortButton label="Category" field="category" currentField={sortField} currentDirection={sortDir} onSort={handleCashflowSort} />
              <SortButton label="Type" field="type" currentField={sortField} currentDirection={sortDir} onSort={handleCashflowSort} />
            </div>
          </div>

          {/* Cashflow Ledger Table */}
          <Card className="bg-white dark:bg-brand-surface border-slate-200 dark:border-brand-border">
            <CardHeader className="py-3 px-4 border-b border-slate-100 dark:border-brand-border flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-800 dark:text-white">
                Cashflow Ledger
              </CardTitle>
              <Badge variant="secondary">{sortedFinances.length} records</Badge>
            </CardHeader>
            <div className="divide-y divide-slate-100 dark:divide-brand-border">
              {financesLoading ? (
                <SkeletonTable rows={5} cols={4} />
              ) : paginatedFinances.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">
                  No financial records found matching criteria.
                </div>
              ) : (
                paginatedFinances.map((tx) => (
                  <div
                    key={tx.id}
                    className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs hover:bg-slate-50/70 dark:hover:bg-brand-surface/40 transition-colors"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Badge
                          variant={tx.type === 'income' ? 'default' : 'secondary'}
                          className="uppercase text-[10px]"
                        >
                          {tx.type}
                        </Badge>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{tx.category}</span>
                        {tx.project_name && <span className="text-slate-400">• {tx.project_name}</span>}
                        {tx.status && (
                          <Badge variant={tx.status as any} className="text-[10px] capitalize">
                            {tx.status}
                          </Badge>
                        )}
                      </div>
                      {tx.notes && <p className="text-slate-500 dark:text-brand-muted">{tx.notes}</p>}
                    </div>

                    <div className="flex items-center space-x-4">
                      <span className="text-slate-400 font-mono">{tx.date}</span>
                      <span
                        className={`font-mono font-bold text-sm ${
                          tx.type === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-800 dark:text-slate-200'
                        }`}
                      >
                        {tx.currency || activeCurrency} {Number(tx.amount).toLocaleString()}
                      </span>
                      <div className="flex items-center space-x-1 pl-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleOpenEditTx(tx)}
                          className="h-7 w-7 text-slate-400 hover:text-brand-navy dark:hover:text-white"
                          title="Edit Transaction"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() =>
                            handleDeleteTx(
                              tx.id,
                              `${tx.type.toUpperCase()}: ${tx.category || tx.notes || 'Transaction'} ($${tx.amount})`
                            )
                          }
                          className="h-7 w-7 text-slate-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                          title="Delete Transaction"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <Pagination
              totalItems={sortedFinances.length}
              currentPage={validCashflowPage}
              pageSize={cashflowPageSize}
              onPageChange={setCurrentPage}
            />
          </Card>
        </div>
      )}

      {/* =========================================================================
          MODAL 1: CREATE INVOICE
         ========================================================================= */}
      {showCreateInvoiceModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto animate-in fade-in"
          onClick={() => setShowCreateInvoiceModal(false)}
        >
          <div
            className="bg-white dark:bg-[#12211F] rounded-xl shadow-2xl max-w-3xl w-full p-6 space-y-5 animate-in zoom-in-95 my-8 border border-slate-200 dark:border-brand-border max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-brand-border">
              <div>
                <h2 className="text-lg font-bold text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                  <Receipt className="w-5 h-5 text-brand-teal" />
                  <span>Create New Invoice</span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-brand-muted mt-0.5">
                  Generate an official invoice for milestones, retainers, or deliverables.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateInvoiceModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateInvoiceSubmit} className="space-y-4">
              {/* Row 1: Invoice #, Platform, Currency */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Invoice Number
                  </label>
                  <Input
                    placeholder="Auto-generated (e.g. INV-2026-004)"
                    value={invoiceForm.invoice_number}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                    className="mt-1 h-9 text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Billing Platform
                  </label>
                  <select
                    value={invoiceForm.platform}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, platform: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-3 text-xs text-slate-700 dark:text-slate-200"
                  >
                    <option value="Direct">Direct Client</option>
                    <option value="Upwork">Upwork</option>
                    <option value="Fiverr">Fiverr</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Currency
                  </label>
                  <select
                    value={invoiceForm.currency}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, currency: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-3 text-xs font-mono text-slate-700 dark:text-slate-200"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="PKR">PKR (Rs)</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Client Selection / Details */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 dark:bg-brand-surface/40 p-3 rounded-lg border border-slate-200/80 dark:border-brand-border">
                <div className="sm:col-span-1">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Select Client *
                  </label>
                  <select
                    value={invoiceForm.client_id}
                    onChange={(e) => handleSelectClient(e.target.value)}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2 text-xs text-slate-700 dark:text-slate-200 truncate"
                  >
                    <option value="">-- Choose Existing Client --</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.company ? `(${c.company})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Client Name *
                  </label>
                  <Input
                    required
                    placeholder="e.g. Acme Corp"
                    value={invoiceForm.client_name}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, client_name: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Client Email
                  </label>
                  <Input
                    type="email"
                    placeholder="billing@client.com"
                    value={invoiceForm.client_email}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, client_email: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>

              {/* Row 3: Linked Project & Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Linked Project (Optional)
                  </label>
                  <select
                    value={invoiceForm.project_id}
                    onChange={(e) => handleSelectProject(e.target.value)}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2 text-xs text-slate-700 dark:text-slate-200 truncate"
                  >
                    <option value="">-- None / General Retainer --</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Issue Date *
                  </label>
                  <Input
                    required
                    type="date"
                    value={invoiceForm.issue_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, issue_date: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Due Date *
                  </label>
                  <Input
                    required
                    type="date"
                    value={invoiceForm.due_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>

              {/* Dynamic Line Items Table */}
              <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-brand-border">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Line Items
                  </span>
                  <button
                    type="button"
                    onClick={handleAddLineItem}
                    className="text-xs text-brand-teal hover:underline font-semibold flex items-center space-x-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Item</span>
                  </button>
                </div>

                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {invoiceForm.line_items.map((item, idx) => (
                    <div
                      key={item.id}
                      className="grid grid-cols-12 gap-2 items-center bg-slate-50 dark:bg-brand-surface/40 p-2.5 rounded-lg border border-slate-200 dark:border-brand-border text-xs"
                    >
                      <div className="col-span-12 sm:col-span-6">
                        <Input
                          placeholder="Description of service or deliverable..."
                          value={item.description}
                          onChange={(e) => handleLineItemChange(idx, 'description', e.target.value)}
                          className="h-8 text-xs"
                          required
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(idx, 'quantity', e.target.value)}
                          className="h-8 text-xs font-mono"
                          required
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Unit Price"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(idx, 'unit_price', e.target.value)}
                          className="h-8 text-xs font-mono"
                          required
                        />
                      </div>
                      <div className="col-span-3 sm:col-span-1 text-right font-mono font-bold text-slate-800 dark:text-slate-200 truncate">
                        {invoiceForm.currency} {Number(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                      <div className="col-span-1 sm:col-span-1 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(idx)}
                          className="text-slate-400 hover:text-red-600 transition-colors p-1"
                          title="Remove item"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subtotal, Discount, Tax & Grand Total */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-slate-200 dark:border-brand-border">
                {/* Notes Column */}
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Payment Notes / Terms
                  </label>
                  <textarea
                    rows={3}
                    value={invoiceForm.payment_notes}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, payment_notes: e.target.value })}
                    placeholder="Instructions, bank details, wire instructions..."
                    className="w-full mt-1 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] p-2 text-xs text-slate-700 dark:text-slate-200"
                  />
                </div>

                {/* Calculation Summary Box */}
                <div className="bg-slate-50 dark:bg-brand-surface/60 p-3.5 rounded-lg border border-slate-200 dark:border-brand-border space-y-2 text-xs">
                  <div className="flex justify-between text-slate-600 dark:text-slate-300">
                    <span>Subtotal:</span>
                    <span className="font-mono font-bold">
                      {invoiceForm.currency} {formTotals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-600 dark:text-slate-300">Discount ({invoiceForm.currency}):</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={invoiceForm.discount}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, discount: Number(e.target.value) || 0 })}
                      className="w-24 h-7 text-xs font-mono text-right"
                    />
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-600 dark:text-slate-300">Tax (%):</span>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={invoiceForm.tax_percent}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_percent: Number(e.target.value) || 0 })}
                      className="w-24 h-7 text-xs font-mono text-right"
                    />
                  </div>

                  <div className="flex justify-between text-slate-600 dark:text-slate-300 pt-1 border-t border-slate-200 dark:border-brand-border">
                    <span>Tax Amount:</span>
                    <span className="font-mono">
                      {invoiceForm.currency} {formTotals.taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm font-bold text-brand-navy dark:text-white pt-1 border-t border-slate-200 dark:border-brand-border">
                    <span>Total Amount:</span>
                    <span className="font-mono text-brand-teal">
                      {invoiceForm.currency} {formTotals.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100 dark:border-brand-border">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateInvoiceModal(false)}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action font-semibold flex items-center space-x-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>Issue Invoice</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL 2: RECORD PAYMENT
         ========================================================================= */}
      {showPaymentModal && paymentInvoice && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className="bg-white dark:bg-[#12211F] rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4 animate-in zoom-in-95 border border-slate-200 dark:border-brand-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-brand-border">
              <div>
                <h2 className="text-base font-bold text-brand-navy dark:text-brand-ink flex items-center space-x-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  <span>Record Invoice Payment</span>
                </h2>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  {paymentInvoice.invoice_number} • {paymentInvoice.client_name}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Balances Callout */}
            <div className="grid grid-cols-3 gap-2 bg-slate-50 dark:bg-brand-surface/40 p-3 rounded-lg border border-slate-200 dark:border-brand-border text-center text-xs">
              <div>
                <div className="text-slate-400 text-[10px]">Total Due</div>
                <div className="font-mono font-bold text-slate-700 dark:text-slate-200">
                  {paymentInvoice.currency} {paymentInvoice.total_amount.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-[10px]">Already Paid</div>
                <div className="font-mono font-bold text-emerald-600">
                  {paymentInvoice.currency} {paymentInvoice.amount_paid.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-slate-400 text-[10px]">Remaining Due</div>
                <div className="font-mono font-bold text-amber-600">
                  {paymentInvoice.currency} {paymentInvoice.balance_due.toLocaleString()}
                </div>
              </div>
            </div>

            <form onSubmit={handleRecordPaymentSubmit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Payment Date *
                </label>
                <Input
                  required
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Amount ({paymentInvoice.currency}) *
                  </label>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={paymentInvoice.balance_due > 0 ? paymentInvoice.balance_due : undefined}
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="mt-1 h-9 text-xs font-mono font-bold text-emerald-700"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Payment Method *
                  </label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-2.5 text-xs text-slate-700 dark:text-slate-200"
                  >
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Stripe">Stripe</option>
                    <option value="Payoneer">Payoneer</option>
                    <option value="Upwork">Upwork Escrow</option>
                    <option value="Fiverr">Fiverr Clearance</option>
                    <option value="Cash">Cash</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Transaction Reference (Wire ID, Stripe Ch ID, etc.)
                </label>
                <Input
                  placeholder="e.g. WIRE-872914 or ch_3M..."
                  value={paymentForm.transaction_ref}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transaction_ref: e.target.value })}
                  className="mt-1 h-9 text-xs font-mono"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Payment Memo / Notes
                </label>
                <Input
                  placeholder="Optional memo..."
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100 dark:border-brand-border">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowPaymentModal(false)}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center space-x-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>Record Payment</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL 3: PRINTABLE / PDF-READY INVOICE PREVIEW
         ========================================================================= */}
      {previewInvoice && (
        <div
          className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto animate-in fade-in print:static print:bg-white print:p-0 print:overflow-visible print:block print:z-auto"
          onClick={() => setPreviewInvoice(null)}
        >
          <div
            id="printable-invoice-modal"
            className="bg-white text-slate-900 rounded-xl shadow-2xl max-w-3xl w-full p-8 space-y-6 animate-in zoom-in-95 my-6 max-h-[92vh] overflow-y-auto print:m-0 print:p-6 print:shadow-none print:max-w-none print:w-full print:rounded-none print:max-h-none print:overflow-visible print:border-none print:bg-white print:text-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Toolbar (Hidden when printing) */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-200 no-print print:hidden">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Official Agency Invoice Document
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {/* Send via Email button */}
                <Button
                  size="sm"
                  disabled={sendingInvoiceId === previewInvoice.id}
                  onClick={() => handleSendInvoiceEmail(previewInvoice)}
                  className="bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-semibold flex items-center space-x-1.5"
                  title="Dispatch invoice directly to client email via Hostinger mail engine"
                >
                  {sendingInvoiceId === previewInvoice.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Mail className="w-3.5 h-3.5" />
                  )}
                  <span>{previewInvoice.sent_at ? 'Resend Email' : 'Send via Email'}</span>
                </Button>

                {/* WhatsApp button */}
                <a
                  href={generateWhatsAppPaymentUrl(previewInvoice)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-md transition-colors space-x-1.5"
                  title="Share invoice summary and payment coordinates via WhatsApp"
                >
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                  <span>WhatsApp</span>
                </a>

                <Button
                  size="sm"
                  onClick={() => window.print()}
                  className="bg-brand-teal text-white hover:bg-brand-teal-action text-xs font-semibold flex items-center space-x-1.5"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Print / Save PDF</span>
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPreviewInvoice(null)}
                  className="text-xs"
                >
                  Close
                </Button>
              </div>
            </div>

            {/* Email Dispatch Status Banner */}
            {previewInvoice.sent_at && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3.5 py-2.5 rounded-lg text-xs flex items-center justify-between no-print print:hidden">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>
                    Dispatched to <strong className="font-semibold">{previewInvoice.sent_to_email || previewInvoice.client_email || 'client'}</strong> on{' '}
                    {new Date(previewInvoice.sent_at).toLocaleString('en-US', { timeZone: 'Asia/Karachi' })} PKT
                  </span>
                </div>
                <span className="text-[10px] font-mono uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-bold tracking-wider">
                  Sent
                </span>
              </div>
            )}

            {/* Document Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-slate-200 pb-6">
              <div>
                <div className="flex items-center space-x-2">
                  <div className="h-8 w-8 rounded-lg bg-brand-navy text-brand-teal flex items-center justify-center font-black text-sm">
                    TO
                  </div>
                  <div>
                    <h1 className="text-xl font-black text-brand-navy tracking-tight">
                      TECHOSOLUTION OS
                    </h1>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Cloud Architecture, AI Engineering & Web Systems
                    </p>
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-3 space-y-0.5">
                  <p>Techosolution Agency Portal</p>
                  <p>billing@techosolution.com • https://techosolution.com</p>
                </div>
              </div>

              <div className="sm:text-right">
                <h2 className="text-2xl font-black text-slate-800 tracking-tight font-mono">
                  INVOICE
                </h2>
                <div className="text-sm font-bold font-mono text-brand-teal mt-0.5">
                  #{previewInvoice.invoice_number}
                </div>
                <div className="mt-2 inline-block">
                  {renderStatusBadge(previewInvoice.status)}
                </div>
              </div>
            </div>

            {/* Bill To & Invoice Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs">
              <div className="space-y-1">
                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">
                  Billed To:
                </span>
                <p className="font-bold text-sm text-slate-900">{previewInvoice.client_name}</p>
                {previewInvoice.client_email && (
                  <p className="text-slate-600">{previewInvoice.client_email}</p>
                )}
                <div className="pt-1">
                  <PlatformBadge platform={previewInvoice.platform} size="xs" />
                </div>
              </div>

              <div className="space-y-1.5 sm:text-right">
                <div className="flex justify-between sm:justify-end sm:space-x-4">
                  <span className="text-slate-400 font-medium">Issue Date:</span>
                  <span className="font-mono font-semibold">{previewInvoice.issue_date}</span>
                </div>
                <div className="flex justify-between sm:justify-end sm:space-x-4">
                  <span className="text-slate-400 font-medium">Payment Due:</span>
                  <span className="font-mono font-semibold">{previewInvoice.due_date || 'On Receipt'}</span>
                </div>
                {previewInvoice.project_name && (
                  <div className="flex justify-between sm:justify-end sm:space-x-4">
                    <span className="text-slate-400 font-medium">Project:</span>
                    <span className="font-medium text-slate-800">{previewInvoice.project_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Line Items Table */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="py-2.5 px-3">Item / Description</th>
                    <th className="py-2.5 px-3 text-right">Qty</th>
                    <th className="py-2.5 px-3 text-right">Rate</th>
                    <th className="py-2.5 px-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {previewInvoice.line_items.map((item, idx) => (
                    <tr key={item.id || idx}>
                      <td className="py-3 px-3 font-medium text-slate-800">{item.description}</td>
                      <td className="py-3 px-3 text-right font-mono">{item.quantity}</td>
                      <td className="py-3 px-3 text-right font-mono">
                        {previewInvoice.currency} {Number(item.unit_price).toLocaleString()}
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-semibold">
                        {previewInvoice.currency} {Number(item.amount).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Calculations Breakdown */}
            <div className="flex flex-col sm:flex-row justify-between gap-6 pt-2">
              <div className="max-w-xs space-y-2 text-xs">
                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">
                  Payment Notes & Instructions:
                </span>
                <p className="text-slate-600 italic bg-slate-50 p-2.5 rounded border border-slate-200 leading-relaxed whitespace-pre-wrap">
                  {previewInvoice.payment_notes || 'Thank you for partnering with Techosolution.'}
                </p>
              </div>

              <div className="w-full sm:w-64 space-y-2 text-xs">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal:</span>
                  <span className="font-mono font-medium">
                    {previewInvoice.currency} {Number(previewInvoice.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {previewInvoice.discount > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Discount:</span>
                    <span className="font-mono text-emerald-600">
                      -{previewInvoice.currency} {Number(previewInvoice.discount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {previewInvoice.tax_amount > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Tax ({previewInvoice.tax_percent}%):</span>
                    <span className="font-mono">
                      {previewInvoice.currency} {Number(previewInvoice.tax_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold text-slate-900 pt-2 border-t border-slate-200">
                  <span>Total Amount:</span>
                  <span className="font-mono text-brand-navy">
                    {previewInvoice.currency} {Number(previewInvoice.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Amount Paid:</span>
                  <span className="font-mono text-emerald-600">
                    {previewInvoice.currency} {Number(previewInvoice.amount_paid).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-bold pt-2 border-t-2 border-slate-800">
                  <span>Balance Due:</span>
                  <span
                    className={`font-mono ${
                      previewInvoice.balance_due > 0 ? 'text-amber-600' : 'text-emerald-600'
                    }`}
                  >
                    {previewInvoice.currency} {Number(previewInvoice.balance_due).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Payments History List */}
            {previewInvoice.payments && previewInvoice.payments.length > 0 && (
              <div className="pt-4 border-t border-slate-200 space-y-2">
                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">
                  Payments Received:
                </span>
                <div className="space-y-1.5">
                  {previewInvoice.payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between text-xs bg-emerald-50/60 border border-emerald-200 p-2 rounded"
                    >
                      <div className="flex items-center space-x-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="font-medium text-emerald-900">{p.payment_method}</span>
                        {p.transaction_ref && (
                          <span className="font-mono text-[11px] text-emerald-700">({p.transaction_ref})</span>
                        )}
                        <span className="text-slate-400 font-mono text-[10px]">• {p.payment_date}</span>
                      </div>
                      <div className="font-mono font-bold text-emerald-700">
                        {previewInvoice.currency} {Number(p.amount).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL 4: ADD CASHFLOW TRANSACTION
         ========================================================================= */}
      {showModal && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white dark:bg-[#12211F] rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in zoom-in-95 border border-slate-200 dark:border-brand-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy dark:text-brand-ink">Record Transaction</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateTx} className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Type</label>
                  <select
                    value={newTx.type}
                    onChange={(e) => setNewTx({ ...newTx, type: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-3 text-xs text-slate-700 dark:text-slate-200"
                  >
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Currency</label>
                  <select
                    value={newTx.currency}
                    onChange={(e) => setNewTx({ ...newTx, currency: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-3 text-xs text-slate-700 dark:text-slate-200"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="PKR">PKR (Rs)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Amount *</label>
                  <Input
                    required
                    type="number"
                    value={newTx.amount}
                    onChange={(e) => setNewTx({ ...newTx, amount: e.target.value })}
                    placeholder="e.g. 5000"
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Category *</label>
                <Input
                  required
                  value={newTx.category}
                  onChange={(e) => setNewTx({ ...newTx, category: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Date *</label>
                <Input
                  required
                  type="date"
                  value={newTx.date}
                  onChange={(e) => setNewTx({ ...newTx, date: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Notes</label>
                <Input
                  value={newTx.notes}
                  onChange={(e) => setNewTx({ ...newTx, notes: e.target.value })}
                  placeholder="Optional memo"
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100 dark:border-brand-border">
                <Button type="button" variant="ghost" onClick={() => setShowModal(false)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Save Transaction
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL 5: EDIT CASHFLOW TRANSACTION
         ========================================================================= */}
      {editingTx && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setEditingTx(null)}
        >
          <div
            className="bg-white dark:bg-[#12211F] rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in zoom-in-95 border border-slate-200 dark:border-brand-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy dark:text-brand-ink">Edit Transaction</h2>
              <button onClick={() => setEditingTx(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveEditTx} className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Type</label>
                  <select
                    value={editForm.type}
                    onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-3 text-xs text-slate-700 dark:text-slate-200"
                  >
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Currency</label>
                  <select
                    value={editForm.currency}
                    onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                    className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-brand-border bg-white dark:bg-[#152724] px-3 text-xs text-slate-700 dark:text-slate-200"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="PKR">PKR (Rs)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Amount *</label>
                  <Input
                    required
                    type="number"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Category *</label>
                <Input
                  required
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Date *</label>
                <Input
                  required
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Notes</label>
                <Input
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100 dark:border-brand-border">
                <Button type="button" variant="ghost" onClick={() => setEditingTx(null)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared Delete Confirmation Dialogs */}
      <DeleteConfirmModal
        isOpen={!!deleteInvoiceTarget}
        itemType="Invoice"
        itemName={deleteInvoiceTarget?.name}
        onClose={() => setDeleteInvoiceTarget(null)}
        onConfirm={confirmDeleteInvoice}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTxTarget}
        itemType="Transaction"
        itemName={deleteTxTarget?.name}
        onClose={() => setDeleteTxTarget(null)}
        onConfirm={confirmDeleteTx}
      />
    </div>
  );
}
